import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { PhysicalSessionDraft, SessionStepProps } from "../types";
import {
  SiteListItem,
  formatSiteAddress,
  useSitesList,
} from "../hooks/useLocations";

const PAGE_SIZE = 10;

const SiteStep: React.FC<SessionStepProps<PhysicalSessionDraft>> = ({
  data,
  setData,
  errors,
  corporateId,
  disabledFields,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { items, totalItems, totalPages, isLoading } = useSitesList({
    corporateId,
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
  });

  const selectedIds = useMemo(
    () => new Set<string | number>(data.site ? [String(data.site.id)] : []),
    [data.site],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((s) => String(s.id) === String(id));
      if (!found) return;
      setData({ site: { id, label: found.name || String(id) }, rooms: [] });
    } else {
      setData({ site: null, rooms: [] });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {errors.site && (
        <Typography sx={{ fontSize: "12px", color: theme.palette.error.main }}>
          {errors.site}
        </Typography>
      )}
      {disabledFields.has("site") ? (
        <Box
          sx={{
            padding: "12px 16px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            fontSize: "14px",
            color: theme.palette.text.primary,
          }}
        >
          {data.site?.label || "-"}
        </Box>
      ) : (
        <SearchableMultiSelectListCst<SiteListItem>
          selectionMode="single"
          items={items}
          getId={(s) => String(s.id)}
          renderRow={({ item }) => (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {item.name || `#${item.id}`}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.tertiary,
                }}
              >
                {[`ID: ${item.id}`, item.code, formatSiteAddress(item)]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>
            </Box>
          )}
          selectedIds={selectedIds}
          onToggle={handleToggle}
          initialSearchValue={search}
          onSearchSubmit={(v) => {
            setSearch(v);
            setPage(1);
          }}
          searchPlaceholder={String(t("site.search-placeholder"))}
          searchButtonLabel={String(t("people-picker.search-button"))}
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          isLoading={isLoading}
          selectedCountLabel={String(
            t("site.selected", { count: data.site ? 1 : 0 }),
          )}
          emptyLabel={String(t("site.empty"))}
        />
      )}
    </Box>
  );
};

export default SiteStep;
